import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useSession } from '@/Recoil/Session/Session';
import { useKoreanStudyType, usePersonaCharacter, usePersonaRoomPic, CharacterPollingStatus, RoomPicPollingStatus } from '@/Recoil/PersonaTest';

type KoreanStudyTypeContextValue = {
    addressid: string;
    typeid: string;

    // Character
    characterPic:             string | null;
    characterSourceImage:     string | null;
    characterPollingStatus:   CharacterPollingStatus;
    characterError:           string | null;
    startCharacterGeneration: (sourceImage: string) => Promise<string | null>;
    cancelCharacterGeneration: () => void;

    // RoomPic
    roomPic:                 string | null;
    roomPicPollingStatus:    RoomPicPollingStatus;
    roomPicError:            string | null;
    startRoomPicGeneration:  (roomImage: string) => Promise<string | null>;
    cancelRoomPicGeneration: () => void;
};

const KoreanStudyTypeContext = createContext<KoreanStudyTypeContextValue | null>(null);

export const useKoreanStudyTypeContext = () => {
    const context = useContext(KoreanStudyTypeContext);
    if (!context) {
        throw new Error('useKoreanStudyTypeContext must be used within KoreanStudyTypeProvider');
    }
    return context;
};

type Props = {
    children: ReactNode;
    path?: string;
};

export const KoreanStudyTypeProvider: React.FC<Props> = ({ children, path }) => {
    const { session } = useSession();
    const { results } = useKoreanStudyType();

    // Extract addressid from path if provided (e.g., /result/:addressid/:type)
    const pathAddressid = (/\/result\/([^/]+)/.exec(path || "") || [null, null])[1];
    const addressid = pathAddressid || session?.addressid || '';
    const typeid = results[addressid]?.type ?? 'casual_learner';

    const {
        characterPic,
        sourceImage: characterSourceImage,
        pollingStatus: characterPollingStatus,
        error: characterError,
        startGeneration: startCharacterGeneration,
        cancelGeneration: cancelCharacterGeneration,
    } = usePersonaCharacter(addressid);

    const {
        roomPic,
        pollingStatus: roomPicPollingStatus,
        error: roomPicError,
        startGeneration: startRoomPicGeneration,
        cancelGeneration: cancelRoomPicGeneration,
    } = usePersonaRoomPic(addressid, typeid);

    // Debug log
    useEffect(() => {
        if (characterPollingStatus === 'polling') {
            console.log('[KoreanStudyTypeContext] Character generation polling...');
        }
        if (roomPicPollingStatus === 'polling') {
            console.log('[KoreanStudyTypeContext] RoomPic generation polling...');
        }
    }, [characterPollingStatus, roomPicPollingStatus]);

    const value: KoreanStudyTypeContextValue = {
        addressid,
        typeid,

        characterPic,
        characterSourceImage,
        characterPollingStatus,
        characterError,
        startCharacterGeneration,
        cancelCharacterGeneration,

        roomPic,
        roomPicPollingStatus,
        roomPicError,
        startRoomPicGeneration,
        cancelRoomPicGeneration,
    };

    return (
        <KoreanStudyTypeContext.Provider value={value}>
            {children}
        </KoreanStudyTypeContext.Provider>
    );
};
